import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Theme } from '@material-ui/core';
import { IncidentKind } from './homeAnnouncements';

/** Backend plugin id of `@internal/plugin-home-visits-backend`, which stores the colours. */
const HOME_VISITS_PLUGIN_ID = 'home-visits';

/**
 * Outline colours an admin can pick for a Home announcement. Keys must match
 * `ANNOUNCEMENT_COLOR_KEYS` in the home-visits backend. Dark mode uses lighter tints so the
 * outline keeps its contrast on dark surfaces.
 */
export const ANNOUNCEMENT_PALETTE = {
  blue: { label: 'Blue', light: '#2969F2', dark: '#6E9BFF' },
  teal: { label: 'Teal', light: '#00A3A3', dark: '#3CCFCF' },
  green: { label: 'Green', light: '#2E9E5B', dark: '#5CC98A' },
  amber: { label: 'Amber', light: '#E8A317', dark: '#F5C04A' },
  orange: { label: 'Orange', light: '#F26B1D', dark: '#FF9455' },
  red: { label: 'Red', light: '#D93636', dark: '#FF6B6B' },
  purple: { label: 'Purple', light: '#7E57C2', dark: '#A98BE0' },
  pink: { label: 'Pink', light: '#D63384', dark: '#F06AAE' },
  slate: { label: 'Slate', light: '#64748B', dark: '#94A3B8' },
} as const;

export type AnnouncementColorKey = keyof typeof ANNOUNCEMENT_PALETTE;

export const ANNOUNCEMENT_COLOR_KEYS = Object.keys(ANNOUNCEMENT_PALETTE) as AnnouncementColorKey[];

export function isAnnouncementColorKey(value: unknown): value is AnnouncementColorKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ANNOUNCEMENT_PALETTE, value);
}

/** Hex value of a palette colour for the current theme type. */
export function paletteColor(key: AnnouncementColorKey, theme: Theme): string {
  const swatch = ANNOUNCEMENT_PALETTE[key];
  return theme.palette.type === 'dark' ? swatch.dark : swatch.light;
}

/**
 * Outline colour of an announcement: the colour an admin picked, otherwise one derived from the
 * category (red for outages, amber for maintenance, the theme's primary colour otherwise).
 */
export function resolveAnnouncementColor(
  theme: Theme,
  chosen: AnnouncementColorKey | undefined,
  incidentKind: IncidentKind | undefined,
): string {
  if (chosen) return paletteColor(chosen, theme);
  if (incidentKind === 'outage') return theme.palette.error.main;
  if (incidentKind === 'maintenance') return theme.palette.warning.main;
  return theme.palette.primary.main;
}

/** Chosen colours of the given announcements. Unknown keys are dropped. */
export async function fetchAnnouncementColors(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  ids: string[],
): Promise<Record<string, AnnouncementColorKey>> {
  if (ids.length === 0) return {};
  const baseUrl = await discoveryApi.getBaseUrl(HOME_VISITS_PLUGIN_ID);
  const response = await fetchApi.fetch(
    `${baseUrl}/announcement-colors?ids=${ids.map(encodeURIComponent).join(',')}`,
  );
  if (!response.ok) {
    throw new Error(`Failed to load announcement colours (HTTP ${response.status})`);
  }
  const body = (await response.json()) as { colors?: Record<string, unknown> };
  const colors: Record<string, AnnouncementColorKey> = {};
  for (const [id, color] of Object.entries(body.colors ?? {})) {
    if (isAnnouncementColorKey(color)) colors[id] = color;
  }
  return colors;
}

/** Sets the outline colour of an announcement; `null` resets it to the category colour. */
export async function saveAnnouncementColor(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  id: string,
  color: AnnouncementColorKey | null,
): Promise<void> {
  const baseUrl = await discoveryApi.getBaseUrl(HOME_VISITS_PLUGIN_ID);
  const response = await fetchApi.fetch(`${baseUrl}/announcement-colors/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ color }),
  });
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? 'You are not allowed to change announcement colours'
        : `Failed to save the announcement colour (HTTP ${response.status})`,
    );
  }
}
